import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { AUTH_ENDPOINTS, HTTP_STATUS } from '@/types/auth';
import { API_CONFIG } from '@/constants';

/** Extended request config with optional metadata for request tracking */
interface RequestConfigWithMetadata extends InternalAxiosRequestConfig {
  metadata?: {
    requestId?: string;
    startTime?: number;
  };
  _retry?: boolean;
}

// In dev use relative path so Vite proxy forwards to backend (avoids CORS/network errors)
const BASE_URL =
  import.meta.env.DEV
    ? '/api/v1'
    : (import.meta.env.VITE_API_BASE_URL ||
        API_CONFIG.BASE_URL ||
        'http://localhost:3001/api/v1');

/**
 * Create Axios instance with default configuration
 * Tokens are now stored in HTTP-only cookies set by the backend
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: API_CONFIG.TIMEOUT || 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for HTTP-only cookies
});

// Token refresh management
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

/**
 * Refresh access token via HTTP-only cookie
 * The backend will read the refresh token from the cookie and set a new access token cookie
 */
const refreshAccessToken = async (): Promise<void> => {
  try {
    await axios.post(
      `${BASE_URL}${AUTH_ENDPOINTS.REFRESH_TOKEN}`,
      {},
      { withCredentials: true }
    );
  } catch (error) {
    // Don't redirect here - let the caller handle it
    throw error;
  }
};

/**
 * Request interceptor
 * Credentials are automatically included via HTTP-only cookies
 */
axiosInstance.interceptors.request.use(
  (config: RequestConfigWithMetadata) => {
    // Add request ID for tracking
    config.metadata = {
      ...config.metadata,
      requestId: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      startTime: Date.now(),
    };

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`🚀 API Request [${config.metadata.requestId}]:`, {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles token refresh and error responses
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      const requestId = response.config.metadata?.requestId;
      const duration = response.config.metadata?.startTime
        ? Date.now() - response.config.metadata.startTime
        : 0;

      console.log(`✅ API Response [${requestId}] (${duration}ms):`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | RequestConfigWithMetadata
      | undefined;

    // Log error in development
    if (import.meta.env.DEV) {
      const requestId = originalRequest?.metadata?.requestId;
      console.error(`❌ API Error [${requestId}]:`, {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
      });
    }

    // Handle 401 Unauthorized
    if (
      error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
      originalRequest
    ) {
      // Prevent infinite loops
      if (originalRequest._retry) {
        const authPages = ['/login', '/register', '/forgot-password', '/reset-password'];
        const publicPages = ['/'];
        const isOnAuthPage = authPages.some((page) => window.location.pathname.includes(page));
        const isOnPublicPage = publicPages.some((page) => window.location.pathname === page);
        if (!isOnAuthPage && !isOnPublicPage) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // Don't retry for auth endpoints - just reject the error
      const authEndpointsToSkip = [
        AUTH_ENDPOINTS.LOGIN,
        AUTH_ENDPOINTS.REGISTER,
        AUTH_ENDPOINTS.FORGOT_PASSWORD,
        AUTH_ENDPOINTS.RESET_PASSWORD,
        // Note: PROFILE endpoint is NOT skipped - we want to refresh token if it's expired
        AUTH_ENDPOINTS.REFRESH_TOKEN,
      ];

      const isAuthEndpoint = authEndpointsToSkip.some((endpoint) =>
        originalRequest.url?.includes(endpoint)
      );

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Handle concurrent refresh requests
        if (isRefreshing) {
          if (refreshPromise) {
            await refreshPromise;
            // Retry the original request - cookies will be sent automatically
            return axiosInstance(originalRequest);
          }
        } else {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();

          await refreshPromise;

          isRefreshing = false;
          refreshPromise = null;

          // Retry the original request - cookies will be sent automatically
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        isRefreshing = false;
        refreshPromise = null;
        const authPages = ['/login', '/register', '/forgot-password', '/reset-password'];
        const publicPages = ['/'];
        const isOnAuthPage = authPages.some((page) => window.location.pathname.includes(page));
        const isOnPublicPage = publicPages.some((page) => window.location.pathname === page);
        if (!isOnAuthPage && !isOnPublicPage) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other status codes with proper error messages
    if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
      console.error('Access denied: Insufficient permissions');
    } else if (error.response?.status === HTTP_STATUS.NOT_FOUND) {
      console.error('Resource not found');
    } else if (error.response?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
      console.error('Validation error:', error.response.data);
    } else if (error.response?.status === HTTP_STATUS.LOCKED) {
      console.error('Account locked');
    } else if (error.response?.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
      console.error('Rate limit exceeded');
    } else if (error.response?.status && error.response.status >= 500) {
      console.error('Server error occurred');
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API errors consistently
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message || error.message;

    switch (error.response?.status) {
      case HTTP_STATUS.BAD_REQUEST:
        return message || 'Invalid request';
      case HTTP_STATUS.UNAUTHORIZED:
        return 'Please log in again';
      case HTTP_STATUS.FORBIDDEN:
        return 'You do not have permission to perform this action';
      case HTTP_STATUS.NOT_FOUND:
        return 'Resource not found';
      case HTTP_STATUS.CONFLICT:
        return message || 'Resource already exists';
      case HTTP_STATUS.UNPROCESSABLE_ENTITY:
        return message || 'Validation failed';
      case HTTP_STATUS.LOCKED:
        return 'Account is temporarily locked';
      case HTTP_STATUS.TOO_MANY_REQUESTS:
        return 'Too many requests. Please try again later';
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        return 'Server error. Please try again later';
      default:
        return message || 'An unexpected error occurred';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

export default axiosInstance;
